/**
 * TanStack Query hooks for the caller's family's children.
 *
 * Separate query key from the family cache (`["children","list"]`)
 * because a family rename should NOT refetch children, and adding a
 * child should not refetch the family row. Mutations invalidate the
 * children cache and nothing else.
 *
 * Photos never appear in any of these payloads — they live in
 * IndexedDB via `lib/photos.ts`. These hooks deal only with the
 * server-side Child rows.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";

export interface Child {
  id: string;
  family_id: string;
  name: string;
  birth_year: number;
  birth_month: number | null;
  birth_day: number | null;
  notes: string | null;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface CreateChildInput {
  name: string;
  birth_year: number;
  birth_month?: number | null;
  birth_day?: number | null;
  notes?: string | null;
}

export interface UpdateChildInput {
  name?: string;
  birth_year?: number;
  birth_month?: number | null;
  birth_day?: number | null;
  notes?: string | null;
}

export const CHILDREN_QUERY_KEY = ["children", "list"] as const;

export function useChildren() {
  return useQuery<Child[], Error>({
    queryKey: CHILDREN_QUERY_KEY,
    queryFn: () => apiFetch<Child[]>("/api/children"),
    staleTime: 30_000,
  });
}

export function useCreateChild() {
  const queryClient = useQueryClient();
  return useMutation<Child, Error, CreateChildInput>({
    mutationFn: (input) =>
      apiFetch<Child>("/api/children", {
        method: "POST",
        body: input,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHILDREN_QUERY_KEY });
    },
  });
}

export function useUpdateChild() {
  const queryClient = useQueryClient();
  return useMutation<Child, Error, { id: string; patch: UpdateChildInput }>({
    mutationFn: ({ id, patch }) =>
      apiFetch<Child>(`/api/children/${id}`, {
        method: "PATCH",
        body: patch,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHILDREN_QUERY_KEY });
    },
  });
}

export function useDeleteChild() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiFetch<void>(`/api/children/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: CHILDREN_QUERY_KEY });
    },
  });
}

/**
 * Reorder mutation — full-list contract per Decision 1.
 *
 * Optimistic update: we replace the cache with the reordered rows
 * immediately so the UI snaps into place without waiting for the
 * server round-trip. On error we roll back by invalidating the
 * query, which refetches from the server. On success we take the
 * server's response as authoritative because it's the source of
 * truth for the new `display_order` values.
 */
export function useReorderChildren() {
  const queryClient = useQueryClient();
  return useMutation<
    Child[],
    Error,
    { child_ids: string[] },
    { previous: Child[] | undefined }
  >({
    mutationFn: ({ child_ids }) =>
      apiFetch<Child[]>("/api/children/reorder", {
        method: "POST",
        body: { child_ids },
      }),
    onMutate: async ({ child_ids }) => {
      await queryClient.cancelQueries({ queryKey: CHILDREN_QUERY_KEY });
      const previous = queryClient.getQueryData<Child[]>(CHILDREN_QUERY_KEY);
      if (previous !== undefined) {
        const byId = new Map(previous.map((c) => [c.id, c]));
        const reordered: Child[] = [];
        for (const [index, id] of child_ids.entries()) {
          const child = byId.get(id);
          if (child !== undefined) {
            reordered.push({ ...child, display_order: index });
          }
        }
        queryClient.setQueryData<Child[]>(CHILDREN_QUERY_KEY, reordered);
      }
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(CHILDREN_QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: CHILDREN_QUERY_KEY });
    },
  });
}
